import React, { FormEvent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { Patient, PatientFormData, DuplicateWarning } from '../types/patient';
import {
  validateSAIdNumber,
  validateSAPhone,
  validateEmail,
  validateRequired,
  dobFromSAId,
} from '../utils/validation';

const STEPS = [
  'Personal Details',
  'Contact Information',
  'Medical Aid',
  'Obstetric History',
  'POPIA Consent',
] as const;

const INITIAL_FORM: PatientFormData = {
  firstName: '',
  lastName: '',
  idNumber: '',
  dateOfBirth: '',
  gender: 'female',
  title: 'Mrs',
  phone: '',
  email: '',
  address: '',
  city: '',
  postalCode: '',
  nextOfKin: { name: '', relationship: '', phone: '' },
  medicalAid: { scheme: '', plan: '', memberNumber: '', mainMember: '', dependantCode: '' },
  isMedicalAidPatient: false,
  obstetricHistory: { gravida: 0, para: 0, miscarriages: 0, ectopics: 0, lmp: '', edd: '', notes: '', bloodGroup: '', rhFactor: '' },
  popiaConsent: { consented: false, consentTimestamp: '', consentMethod: 'in-person' },
};

export function PatientRegisterPage() {
  const navigate = useNavigate();
  const { post } = useApi();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<PatientFormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [duplicates, setDuplicates] = useState<DuplicateWarning[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const updateField = useCallback(<K extends keyof PatientFormData>(field: K, value: PatientFormData[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const updateNested = useCallback(<P extends 'nextOfKin' | 'medicalAid' | 'obstetricHistory' | 'popiaConsent'>(
    parent: P,
    field: string,
    value: unknown,
  ) => {
    setForm(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value },
    }));
  }, []);

  // Auto-fill DOB from ID number
  const handleIdNumberChange = useCallback((value: string) => {
    updateField('idNumber', value);
    if (value.replace(/\s/g, '').length === 13) {
      const dob = dobFromSAId(value);
      if (dob) updateField('dateOfBirth', dob);
    }
  }, [updateField]);

  const validateStep = useCallback((stepIndex: number): boolean => {
    const errs: Record<string, string> = {};

    if (stepIndex === 0) {
      const fn = validateRequired(form.firstName, 'First name');
      if (!fn.valid) errs.firstName = fn.error!;
      const ln = validateRequired(form.lastName, 'Last name');
      if (!ln.valid) errs.lastName = ln.error!;
      const id = validateSAIdNumber(form.idNumber);
      if (!id.valid) errs.idNumber = id.error!;
      if (!form.dateOfBirth) errs.dateOfBirth = 'Date of birth is required';
    }

    if (stepIndex === 1) {
      const ph = validateSAPhone(form.phone);
      if (!ph.valid) errs.phone = ph.error!;
      const em = validateEmail(form.email);
      if (!em.valid) errs.email = em.error!;
    }

    if (stepIndex === 2 && form.isMedicalAidPatient) {
      if (!form.medicalAid.scheme) errs['medicalAid.scheme'] = 'Scheme is required';
      if (!form.medicalAid.memberNumber) errs['medicalAid.memberNumber'] = 'Member number is required';
    }

    if (stepIndex === 4 && !form.popiaConsent.consented) {
      errs['popiaConsent.consented'] = 'POPIA consent is required to register';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [form]);

  const handleNext = useCallback(() => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, STEPS.length - 1));
    }
  }, [step, validateStep]);

  const handleBack = useCallback(() => {
    setStep(prev => Math.max(prev - 1, 0));
  }, []);

  const handleSubmit = useCallback(async (e: FormEvent, force = false) => {
    e.preventDefault();
    if (!validateStep(step)) return;

    // Set consent timestamp
    const submitData = {
      ...form,
      popiaConsent: {
        ...form.popiaConsent,
        consentTimestamp: new Date().toISOString(),
      },
    };

    setSubmitting(true);
    setServerError('');
    setDuplicates([]);

    try {
      const url = force ? '/patients?force=true' : '/patients';
      const result = await post<Patient>(url, submitData);

      if (result.success && result.data) {
        navigate(`/patients/${result.data.id}`);
      } else if (result.error && (result.data as unknown as { duplicates?: DuplicateWarning[] })?.duplicates) {
        setDuplicates((result.data as unknown as { duplicates: DuplicateWarning[] }).duplicates);
      } else {
        setServerError(result.error || 'Failed to register patient');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setServerError(axiosErr.response?.data?.error || 'Failed to register patient');
    } finally {
      setSubmitting(false);
    }
  }, [form, step, validateStep, post, navigate]);

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const errorClass = 'text-red-600 text-xs mt-1';

  const renderStep0 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Title</label>
          <select value={form.title} onChange={e => updateField('title', e.target.value as PatientFormData['title'])} className={inputClass}>
            <option value="Mrs">Mrs</option>
            <option value="Ms">Ms</option>
            <option value="Miss">Miss</option>
            <option value="Dr">Dr</option>
            <option value="Prof">Prof</option>
            <option value="Mr">Mr</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Gender</label>
          <select value={form.gender} onChange={e => updateField('gender', e.target.value as PatientFormData['gender'])} className={inputClass}>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>First Name *</label>
          <input value={form.firstName} onChange={e => updateField('firstName', e.target.value)} className={inputClass} />
          {errors.firstName && <p className={errorClass}>{errors.firstName}</p>}
        </div>
        <div>
          <label className={labelClass}>Last Name *</label>
          <input value={form.lastName} onChange={e => updateField('lastName', e.target.value)} className={inputClass} />
          {errors.lastName && <p className={errorClass}>{errors.lastName}</p>}
        </div>
      </div>
      <div>
        <label className={labelClass}>SA ID Number *</label>
        <input value={form.idNumber} onChange={e => handleIdNumberChange(e.target.value)} maxLength={13} className={inputClass} placeholder="e.g. 8501015009087" />
        {errors.idNumber && <p className={errorClass}>{errors.idNumber}</p>}
      </div>
      <div>
        <label className={labelClass}>Date of Birth *</label>
        <input type="date" value={form.dateOfBirth} onChange={e => updateField('dateOfBirth', e.target.value)} className={inputClass} />
        {errors.dateOfBirth && <p className={errorClass}>{errors.dateOfBirth}</p>}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Phone</label>
          <input value={form.phone} onChange={e => updateField('phone', e.target.value)} className={inputClass} placeholder="0821234567" />
          {errors.phone && <p className={errorClass}>{errors.phone}</p>}
        </div>
        <div>
          <label className={labelClass}>Email</label>
          <input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} className={inputClass} />
          {errors.email && <p className={errorClass}>{errors.email}</p>}
        </div>
      </div>
      <div>
        <label className={labelClass}>Address</label>
        <input value={form.address} onChange={e => updateField('address', e.target.value)} className={inputClass} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>City</label>
          <input value={form.city} onChange={e => updateField('city', e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Postal Code</label>
          <input value={form.postalCode} onChange={e => updateField('postalCode', e.target.value)} className={inputClass} />
        </div>
      </div>
      <fieldset className="border border-gray-200 rounded-md p-4">
        <legend className="text-sm font-medium text-gray-700 px-2">Next of Kin</legend>
        <div className="space-y-3">
          <div>
            <label className={labelClass}>Name</label>
            <input value={form.nextOfKin.name} onChange={e => updateNested('nextOfKin', 'name', e.target.value)} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Relationship</label>
              <input value={form.nextOfKin.relationship} onChange={e => updateNested('nextOfKin', 'relationship', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input value={form.nextOfKin.phone} onChange={e => updateNested('nextOfKin', 'phone', e.target.value)} className={inputClass} />
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.isMedicalAidPatient}
          onChange={e => updateField('isMedicalAidPatient', e.target.checked)}
          className="h-4 w-4 text-blue-600 rounded"
        />
        <span className="text-sm font-medium text-gray-700">Patient is on medical aid</span>
      </label>
      {form.isMedicalAidPatient && (
        <div className="space-y-4 pl-6 border-l-2 border-blue-200">
          <div>
            <label className={labelClass}>Scheme *</label>
            <input value={form.medicalAid.scheme} onChange={e => updateNested('medicalAid', 'scheme', e.target.value)} className={inputClass} placeholder="e.g. Discovery Health" />
            {errors['medicalAid.scheme'] && <p className={errorClass}>{errors['medicalAid.scheme']}</p>}
          </div>
          <div>
            <label className={labelClass}>Plan</label>
            <input value={form.medicalAid.plan} onChange={e => updateNested('medicalAid', 'plan', e.target.value)} className={inputClass} placeholder="e.g. Classic Comprehensive" />
          </div>
          <div>
            <label className={labelClass}>Member Number *</label>
            <input value={form.medicalAid.memberNumber} onChange={e => updateNested('medicalAid', 'memberNumber', e.target.value)} className={inputClass} />
            {errors['medicalAid.memberNumber'] && <p className={errorClass}>{errors['medicalAid.memberNumber']}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Main Member Name</label>
              <input value={form.medicalAid.mainMember} onChange={e => updateNested('medicalAid', 'mainMember', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Dependant Code</label>
              <input value={form.medicalAid.dependantCode} onChange={e => updateNested('medicalAid', 'dependantCode', e.target.value)} className={inputClass} placeholder="e.g. 00, 01" />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-4">
        <div>
          <label className={labelClass}>Gravida</label>
          <input type="number" min={0} value={form.obstetricHistory.gravida} onChange={e => updateNested('obstetricHistory', 'gravida', parseInt(e.target.value) || 0)} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Para</label>
          <input type="number" min={0} value={form.obstetricHistory.para} onChange={e => updateNested('obstetricHistory', 'para', parseInt(e.target.value) || 0)} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Miscarriages</label>
          <input type="number" min={0} value={form.obstetricHistory.miscarriages} onChange={e => updateNested('obstetricHistory', 'miscarriages', parseInt(e.target.value) || 0)} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Ectopics</label>
          <input type="number" min={0} value={form.obstetricHistory.ectopics} onChange={e => updateNested('obstetricHistory', 'ectopics', parseInt(e.target.value) || 0)} className={inputClass} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Last Menstrual Period (LMP)</label>
          <input type="date" value={form.obstetricHistory.lmp} onChange={e => updateNested('obstetricHistory', 'lmp', e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Expected Delivery Date (EDD)</label>
          <input type="date" value={form.obstetricHistory.edd} onChange={e => updateNested('obstetricHistory', 'edd', e.target.value)} className={inputClass} />
        </div>
      </div>
      <div>
        <label className={labelClass}>Notes</label>
        <textarea rows={3} value={form.obstetricHistory.notes} onChange={e => updateNested('obstetricHistory', 'notes', e.target.value)} className={inputClass} />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-sm text-blue-800">
        <p className="font-medium mb-2">Protection of Personal Information Act (POPIA) Consent</p>
        <p>
          By consenting, the patient acknowledges that their personal and medical information will be
          collected, stored, and processed by IKhan Gynae Practice for the purposes of providing
          healthcare services, medical billing, and appointment management. All data is stored in
          South Africa (africa-south1 region) and will not be transferred outside the country.
          Patient records are retained for a minimum of 6 years as required by the HPCSA.
        </p>
      </div>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.popiaConsent.consented}
          onChange={e => updateNested('popiaConsent', 'consented', e.target.checked)}
          className="h-4 w-4 text-blue-600 rounded"
        />
        <span className="text-sm font-medium text-gray-700">Patient has given consent</span>
      </label>
      {errors['popiaConsent.consented'] && <p className={errorClass}>{errors['popiaConsent.consented']}</p>}
      <div>
        <label className={labelClass}>Consent Method</label>
        <select
          value={form.popiaConsent.consentMethod}
          onChange={e => updateNested('popiaConsent', 'consentMethod', e.target.value)}
          className={inputClass}
        >
          <option value="in-person">In Person</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="email">Email</option>
        </select>
      </div>
    </div>
  );

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4];

  return (
    <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Step indicator */}
        <nav className="mb-8">
          <ol className="flex items-center">
            {STEPS.map((label, i) => (
              <li key={label} className="flex items-center flex-1">
                <div className="flex items-center">
                  <span
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      i < step ? 'bg-blue-600 text-white' :
                      i === step ? 'bg-blue-600 text-white' :
                      'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                  </span>
                  <span className={`ml-2 text-xs hidden sm:inline ${i <= step ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                    {label}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-3 ${i < step ? 'bg-blue-600' : 'bg-gray-200'}`} />
                )}
              </li>
            ))}
          </ol>
        </nav>

        {/* Duplicate warnings */}
        {duplicates.length > 0 && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Possible duplicate patient(s) detected:</p>
                <ul className="mt-2 space-y-1">
                  {duplicates.map((d, i) => (
                    <li key={i} className="text-sm text-yellow-700">
                      {d.existingPatientName} — match by {d.matchType === 'id_number' ? 'ID number' : 'name + date of birth'}
                      <button
                        onClick={() => navigate(`/patients/${d.existingPatientId}`)}
                        className="ml-2 text-blue-600 underline hover:text-blue-800"
                      >
                        View
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={(e) => handleSubmit(e as unknown as FormEvent, true)}
                  disabled={submitting}
                  className="mt-3 px-4 py-1.5 text-sm bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                >
                  Register Anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {serverError && (
          <div className="mb-6 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
            {serverError}
          </div>
        )}

        {/* Form step */}
        <form onSubmit={e => step === STEPS.length - 1 ? handleSubmit(e) : (e.preventDefault(), handleNext())}>
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{STEPS[step]}</h2>
            {stepRenderers[step]()}
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={handleBack}
              disabled={step === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>

            {step < STEPS.length - 1 ? (
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2"
              >
                Next <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Registering...</> : <><Check className="h-4 w-4" /> Register Patient</>}
              </button>
            )}
          </div>
        </form>
    </main>
  );
}
